import type { IGroupChatUser } from "@common/types/api/r2-group-chat-types";
import type { IGroupChatSendResult, IGroupChatUiMessage } from "@core/types/group-chat-message.types";
import { Observable, of } from "rxjs";

/**
 * Contract for the document-scoped, multi-user group chat. Exposed on the Core
 * facade as `core.groupChat`. Mirrors the shape of {@link ChatBase} but the
 * conversation is between human users attached to the current Office document.
 */
export abstract class GroupChatBase
{
    /** Stream of messages for the active document, oldest first. */
    public abstract getMessages(): Observable<IGroupChatUiMessage[]>;
    public abstract getMessagesValue(): IGroupChatUiMessage[];

    /** Stream of participants for the active document. */
    public abstract getUsers(): Observable<IGroupChatUser[]>;

    /** Post a message; pass `parentId` to reply to an existing message. */
    public abstract sendMessage(text: string, parentId?: number | null): Promise<IGroupChatSendResult>;

    /** Delete a single message by its identifier. */
    public abstract deleteMessage(id: number): Promise<void>;

    /** Delete several messages at once. */
    public abstract deleteMessages(ids: number[]): Promise<void>;

    /** Delete every message the current user posted in the document. */
    public abstract clearAll(): Promise<void>;

    /** Load newer participants explicitly (paginated). */
    public abstract loadUsers(): Promise<void>;

    /** Resolve the active document and load its first page of messages. */
    public abstract init(): Promise<void>;

    /** True until the active document has received its first successful history response. */
    public abstract $isMessagesLoading(): Observable<boolean>;

    public $isIdentityUnavailable(): Observable<boolean>
    {
        return of(false);
    }

    public $needsMailConsent(): Observable<boolean>
    {
        return of(false);
    }

    public $documentId(): Observable<string | null>
    {
        return of(null);
    }

    public requestMailConsent(): Promise<void>
    {
        return Promise.resolve();
    }

    /** Whether the active Office document has a usable group-chat identity. */
    public abstract $isAvailable(): Observable<boolean>;

    /**
     * Id of the oldest message from another participant newer than the last-read
     * marker, computed on demand; null when everything was read. Used by the
     * view as its initial scroll anchor.
     */
    public abstract getFirstUnreadMessageId(): number | null;

    public abstract $firstUnreadId(): Observable<number | null>;

    /**
     * Number of messages from other participants newer than the last-read
     * marker — the group-tab notification badge. Recomputed on every load/poll
     * and cleared once the user is focused and scrolled to the bottom.
     */
    public abstract $unreadCount(): Observable<number>;

    /**
     * The add-in window holds focus and is visible. Reported by the conversation
     * shell, so it stays accurate on both conversation tabs.
     */
    public abstract setWindowActive(isActive: boolean): void;

    /**
     * The group chat view is the one on screen. Together with the window state
     * this gates read-marking and how often we poll: an active window showing
     * another view only needs the unread badge, so polling backs off.
     */
    public abstract setViewActive(isActive: boolean): void;

    public abstract setAtBottom(isAtBottom: boolean): void;

    /** Begin/stop background polling for new messages. */
    public abstract startPolling(): void;
    public abstract stopPolling(): void;

    public onDestroy(): void { }
}
